import {
  captureException,
  setUser as setSentryUser,
} from '@sentry/react-router'
import { QueryClient } from '@tanstack/react-query'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { Provider } from 'jotai'
import { useHydrateAtoms } from 'jotai/utils'
import posthog from 'posthog-js'
import { useEffect } from 'react'
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useLocation,
  useRouteError,
  type LinksFunction,
  type LoaderFunctionArgs,
  type MetaFunction,
  type ShouldRevalidateFunctionArgs,
} from 'react-router'
import { aClient, aUser } from '~/atoms'
import { NotFound, ServerError, StackTrace } from '~/components/error'
import { LogoOverlay } from '~/components/layout'
import { createIdbPersister } from '~/lib'
import {
  getCookieToken,
  getSubdomainClient,
  parseTokenUser,
} from '~/lib.server'
import { type Client, type User } from '~/models'
import '~/styles/mapbox-gl.css'
import '~/tailwind.css'

export const links: LinksFunction = () => [
  { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
  {
    rel: 'preconnect',
    href: 'https://fonts.gstatic.com',
    crossOrigin: 'anonymous',
  },
  {
    rel: 'stylesheet',
    href: 'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&family=Source+Sans+3:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&display=swap',
  },
  { rel: 'icon', href: '/favicon.ico', sizes: 'any' },
  { rel: 'apple-touch-icon', href: '/apple-touch-icon.png' },
]

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const title = data?.client ? `${data.client.name}` : 'Not Found'
  return [
    { title: `${title} - Resimplifi View` },
    { property: 'og:title', content: `${title} - Resimplifi View` },
    { property: 'twitter:title', content: `${title} - Resimplifi View` },
    { property: 'og:image', content: 'https://resimplifi.com/open-graph.png' },
    {
      property: 'twitter:image',
      content: 'https://resimplifi.com/open-graph.png',
    },
  ]
}
export const shouldRevalidate = ({
  currentUrl,
}: ShouldRevalidateFunctionArgs) => {
  return currentUrl.pathname === '/login' || currentUrl.pathname === '/redirect'
}

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const client = await getSubdomainClient(request.url)
  if (client === null) {
    throw new Response('Not found', { status: 404 })
  }

  const token = await getCookieToken(request)
  const user = await parseTokenUser(token)

  return { client, user }
}

export function Layout({ children }: { children: React.ReactNode }) {
  const { client } = useLoaderData<typeof loader>()
  const description = client
    ? `${client.name} building and site data`
    : undefined

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <meta name="description" content={description} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="article" />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:description" content={description} />
        <Links />
      </head>
      <body>
        {children}
        <LogoOverlay client={client} />
        <ScrollRestoration />
        <Scripts />
        <script
          async
          src="https://maps.googleapis.com/maps/api/js?key=AIzaSyAv5MD0Ed191OORtaC3YkigbkXkWq3JnOY&libraries=places&callback=Function.prototype&loading=async"
        />
        <DebugScreenSizes />
      </body>
    </html>
  )
}

function HydrationContainer({
  client,
  user,
}: {
  client: Client
  user: User | null
}) {
  useHydrateAtoms([[aClient, client]])
  useHydrateAtoms([[aUser, user]])

  return <Outlet />
}

const queryClient = new QueryClient()

export default function App() {
  const { client, user } = useLoaderData<typeof loader>()
  const location = useLocation()

  useEffect(() => {
    posthog.capture('$pageview')
  }, [location.pathname])

  useEffect(() => {
    if (user) {
      setSentryUser({ email: user.email })
      posthog.identify(user.id, {
        email: user.email,
        name: `${user.first_name} ${user.last_name}`,
      })
    } else {
      setSentryUser(null)
    }
  }, [user])

  useEffect(() => {
    const outer = document.createElement('div')
    outer.style.visibility = 'hidden'
    outer.style.width = '100px'
    document.body.appendChild(outer)

    const widthNoScroll = outer.offsetWidth
    outer.style.overflow = 'scroll'

    const inner = document.createElement('div')
    inner.style.width = '100%'
    outer.appendChild(inner)

    const widthWithScroll = inner.offsetWidth
    outer.parentNode?.removeChild(outer)

    if (widthNoScroll - widthWithScroll > 0) {
      document.documentElement.classList.add('scrollbar')
    }
  }, [])
  return (
    <Provider>
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{
          persister: createIdbPersister(),
          maxAge: 1000 * 60 * 60 * 24 * 5,
        }}>
        <HydrationContainer client={client} user={user} />
      </PersistQueryClientProvider>
    </Provider>
  )
}

export function ErrorBoundary() {
  const error = useRouteError()

  function renderError() {
    if (isRouteErrorResponse(error)) {
      if (error.status === 404) {
        return <NotFound />
      } else {
        return <ServerError response={error} />
      }
    } else if (error instanceof Error) {
      return <StackTrace error={error} />
    } else {
      return <div>Unknown Error</div>
    }
  }

  if (import.meta.env.PROD) {
    captureException(error)
  }

  return renderError()
}

function DebugScreenSizes() {
  if (!import.meta.env.DEV) {
    return null
  }
  return (
    <div className="fixed left-0 top-0 z-[999] bg-red-500 px-2 py-1 text-white print:hidden">
      <span className="sm:hidden">xs</span>
      <span className="hidden sm:inline-block md:hidden">sm</span>
      <span className="hidden md:inline-block lg:hidden">md</span>
      <span className="hidden lg:inline-block xl:hidden">lg</span>
      <span className="hidden xl:inline-block 2xl:hidden">xl</span>
      <span className="hidden 2xl:inline-block">2xl</span>
    </div>
  )
}
